import React, { useState, useEffect } from "react";
import Expense from "../entities/expense";
import { useNavigate, useParams } from "react-router";
import createPageUrl from "../utils/createPageUrl";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { format } from "date-fns";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Calendar,
  Tag,
  CreditCard,
  Type,
  Hash,
} from "lucide-react";
import { Breadcrumb } from "../components/ui/Breadcrumb";

export default function ExpenseDetailsPage() {
  const navigate = useNavigate();
  const [expense, setExpense] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const { id: expenseId } = useParams();

  useEffect(() => {
    if (!expenseId) {
      setError("No expense ID provided.");
      setIsLoading(false);
      return;
    }
    loadExpense();
  }, [expenseId]);

  const loadExpense = async () => {
    try {
      const data = await Expense.get(expenseId);
      setExpense(data);
    } catch (err) {
      setError("Failed to load expense details.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await Expense.delete(expenseId);
      navigate(createPageUrl("expenses"));
    } catch (err) {
      setError("Failed to delete expense.");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate(createPageUrl("expenses"))}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-3xl font-bold text-foreground">
              Expense Details
            </h1>
          </div>
          <Card className="shadow-sm border bg-card p-6 rounded-xl max-w-2xl mx-auto">
            <CardHeader className="p-0 pb-4">
              <Skeleton className="h-8 w-1/3" />
            </CardHeader>
            <CardContent className="space-y-4 p-0 pt-4">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-6 w-3/4" />
            </CardContent>
            <CardFooter className="gap-4 p-0 pt-4 justify-end">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !expense) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8 flex items-center justify-center">
        <div className="text-center text-destructive">
          {error || "Expense not found."}
        </div>
      </div>
    );
  }

  const detailItems = [
    {
      icon: Calendar,
      label: "Date",
      value: format(new Date(expense.date), "MMMM d, yyyy"),
    },
    {
      icon: Tag,
      label: "Category",
      value: (
        <Badge className="text-sm">{expense.category.replace(/_/g, " ")}</Badge>
      ),
    },
    {
      icon: CreditCard,
      label: "Payment Method",
      value: expense.payment_method?.replace(/_/g, " ") || "N/A",
    },
    { icon: Type, label: "Description", value: expense.description },
    {
      icon: Hash,
      label: "Tags",
      value: expense.tags?.length > 0 ? expense.tags.join(", ") : "No tags",
    },
  ];

  return (
    <div className="min-h-screen bg-background pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb />
        <div className="flex items-center gap-4 mb-6">
          <h1 className="text-3xl font-bold text-foreground">
            Expense Details
          </h1>
        </div>
        <Card className="shadow-sm border bg-card p-6 rounded-xl max-w-2xl mx-auto">
          <CardHeader className="p-0 pb-4">
            <CardTitle className="text-3xl font-bold text-foreground">
              -₹{expense.amount.toFixed(2)}
            </CardTitle>
            <p className="text-muted-foreground">{expense.description}</p>
          </CardHeader>
          <CardContent className="space-y-4 p-0 pt-4">
            {detailItems.map((item) => (
              <div key={item.label} className="flex items-start gap-4">
                <item.icon className="w-5 h-5 mt-1 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground capitalize">
                    {item.label}
                  </p>
                  <p className="text-lg font-semibold text-foreground capitalize">
                    {item.value}
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
          <CardFooter className="flex justify-end gap-3 p-0 pt-4">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="gap-2">
                  <Trash2 className="w-4 h-4" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    this expense record.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              className="gap-2 bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
              onClick={() =>
                navigate(createPageUrl(`add-expense?id=${expense.id}`))
              }
            >
              <Edit className="w-4 h-4" /> Edit
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
